import type { Node } from '../../core/models/scene';

export type GroupPath = string[];

export const getNodeGroupPath = (node: Pick<Node, 'attrs'> | null | undefined): GroupPath => {
  const raw = node?.attrs && (node.attrs as any).groupPath;
  if (Array.isArray(raw)) {
    return raw.filter((entry) => typeof entry === 'string' && entry.length > 0);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.includes('/')) {
      return trimmed.split('/').filter(Boolean);
    }
    return [trimmed];
  }
  return [];
};

export const getGroupKeyFromPath = (path: GroupPath): string | null => {
  return path.length > 0 ? path[path.length - 1] : null;
};

export const getGroupPathKeyFromPath = (path: GroupPath): string | null => {
  return path.length > 0 ? path.join('/') : null;
};

export const getNodeGroupKey = (node: Pick<Node, 'attrs'> | null | undefined): string | null => {
  return getGroupKeyFromPath(getNodeGroupPath(node));
};

export const getNodeGroupPathKey = (node: Pick<Node, 'attrs'> | null | undefined): string | null => {
  return getGroupPathKeyFromPath(getNodeGroupPath(node));
};

export const getCommonGroupPrefix = (paths: GroupPath[]): GroupPath => {
  if (paths.length === 0) return [];
  let prefix = [...paths[0]];
  for (let i = 1; i < paths.length; i += 1) {
    const path = paths[i];
    let idx = 0;
    while (idx < prefix.length && idx < path.length && prefix[idx] === path[idx]) {
      idx += 1;
    }
    prefix = prefix.slice(0, idx);
    if (prefix.length === 0) break;
  }
  return prefix;
};

export const expandNodeIdsWithGroupMembers = (nodeIds: Iterable<string>, nodes: Node[]): Set<string> => {
  const expanded = new Set<string>();
  const groupKeys = new Set<string>();

  for (const id of nodeIds) {
    expanded.add(id);
    const node = nodes.find((n) => n.id === id);
    const key = getNodeGroupKey(node);
    if (key) groupKeys.add(key);
  }

  if (groupKeys.size === 0) return expanded;

  nodes.forEach((node) => {
    const key = getNodeGroupKey(node);
    if (key && groupKeys.has(key)) {
      expanded.add(node.id);
    }
  });

  return expanded;
};

export const collectGroupMembersByKey = (groupKeys: Iterable<string>, nodes: Node[]): Set<string> => {
  const keys = new Set(groupKeys);
  const members = new Set<string>();
  if (keys.size === 0) return members;

  nodes.forEach((node) => {
    const key = getNodeGroupKey(node);
    if (key && keys.has(key)) {
      members.add(node.id);
    }
  });

  return members;
};
